<template>
  <div class="medical-record-exams" ref="medicalRecordExamsPage">
    <portal to="page-name">Exames</portal>
    <div class="loading loading-lg" v-if="loadingPatient || loading" />
    <template v-else>
      <record-header
        :patient="patient"
        :loading="loadingPatient"
        :appointment="appointment"
      >
        <button
          class="btn btn-lg btn-gray btn-icon btn-icon-left"
          @click="$router.back()"
        ><fa-icon :icon="['fal', 'arrow-left']" /> Voltar</button>
      </record-header>

      <div class="page-container">
        <div class="card card-page medical-record-form">
          <h6>Exames</h6>

          <div class="card-detail" v-if="hasExpenses">
            <div class="card-detail-title">Serviços agendados</div>
            <div
              class="card-detail-data"
              v-for="(expense, i) in appointment.expenses"
              :key="i"
            >{{ expense.code | tuss }} - {{ expense.name }}</div>
          </div>

          <exam-form
            :medical-record-id="hasOpenRecord ? form.id : null"
            :patient="patient"
            :triage="true"
            :appointment-id="appointmentId"
            :professional-id="professionalId"
            :insurance-plan-id="insurancePlanId"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import formMixin from 'src/mixins/form';
import { mapState, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { mergeFrom } from '@/helpers/object';
import RecordHeader from './record/Header.vue';
import ExamForm from './exam/Form.vue';
import { date, minDate } from '../../../data/validators';

export default {
  mixins: [formMixin],
  components: {
    RecordHeader,
    ExamForm,
  },
  data() {
    return {
      saving: false,
      loading: false,
      loadingPatient: true,
      appointment: {
        expenses: [],
        specialtyCode: 0,
        insurancePlanId: '',
        insuranceName: '',
        professionalId: '',
      },
      patient: {
        id: '',
        name: '',
        socialName: '',
        gender: '',
        identity: {
          type: '',
          value: '',
        },
        birthDate: '',
        phone: '',
        cellphone: '',
        addresses: [],
        imageUrl: '',
        insuranceName: '',
        insuranceId: '',
        insurancePlanId: '',
        motherName: '',
        allergy: '',
        record: '',
        validity: '',
      },
      records: [],
      form: this.formBlank(),
    };
  },
  async mounted() {
    const patientId = this.$route.params.id;
    this.loadAppointment();
    await this.findPatient(patientId);
    await this.loadRecords(patientId);

    if (this.hasOpenRecord) {
      await this.load(this.openRecord.id);
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      branch: state => state.auth.user.branch,
    }),
    ...mapGetters({
      branchSettings: 'branchSettings',
      hasModule: 'hasModule',
    }),
    isLoading() {
      return this.saving || this.pending;
    },
    openRecord() {
      return this.records.find(item => (
        ['triage', 'pending'].includes(item.status)
      ));
    },
    hasOpenRecord() {
      return !!this.openRecord;
    },
    appointmentId() {
      return this.$route.query.appointmentId || null;
    },
    professionalId() {
      if (this.$route.query.professionalId) {
        return this.$route.query.professionalId;
      }
      if (this.appointment?.professionalId) {
        return this.appointment.professionalId;
      }
      return null;
    },
    insurancePlanId() {
      if (this.appointment?.insurancePlanId) {
        return this.appointment.insurancePlanId;
      }
      if (this.patient?.insurancePlanId) {
        return this.patient.insurancePlanId;
      }
      return null;
    },
    hasExpenses() {
      return this.appointment && this.appointment.expenses?.length > 0;
    },
  },
  validations() {
    const rules = {
      form: {
        reminder: {
          date: {},
        },
      },
    };

    if (this.reminder) {
      rules.form.reminder.date = {
        required,
        date,
        minDate: minDate(),
      };
    }

    return rules;
  },
  methods: {
    async loadAppointment() {
      if (this.appointmentId) {
        try {
          const { data } = await this.$httpX.get(`/appointments/${this.appointmentId}`);
          this.appointment.professionalId = data.professional.id;
          this.appointment.specialtyCode = data.professional.specialty?.code;
          this.appointment.insurancePlanId = data.insurance.plan.id;
          this.appointment.insuranceName = this.getCustomName(data.insurance);
          this.appointment.expenses = data.expenses;
        } catch (e) {
          this.showError(e);
        }
      }
    },
    async load(id) {
      this.loading = true;

      try {
        const { data } = await this.$httpX.get(`/medical-records/${id}`);
        const mergeData = data || this.formBlank();
        mergeFrom(this.form, mergeData);
      } catch (e) {
        this.showError(e);
      }

      this.loading = false;
    },
    async loadRecords(patientId) {
      this.loading = true;

      try {
        const { data } = await this.$httpX.get(`/patients/${patientId}/medical-records?limit=2`);
        if (data.items && data.items.length > 0) {
          data.items = data.items.filter(item => (
            !item.private || (item.private && item.professional?.id === this.user.id)
          ));
        }
        this.records = data.items;
      } catch (e) {
        this.showError(e);
      }

      this.loading = false;
    },
    async findPatient(patientId) {
      this.loadingPatient = true;
      try {
        const { data } = await this.$httpX.get(`/patients/${patientId}`);
        const patient = data.patient || {};
        const insurance = patient.insurance || null;

        this.patient = {
          id: data.id,
          name: data.name,
          socialName: data.socialName,
          birthDate: data.birthDate,
          gender: data.gender,
          phone: data.phone,
          cellphone: data.cellphone,
          identity: data.identity,
          imageUrl: data.imageUrl,
          addresses: data.addresses,
          motherName: patient.motherName || '',
          insuranceName: insurance ? this.getCustomName(insurance) : '',
          insuranceId: insurance ? insurance.id : '',
          insurancePlanId: insurance && insurance.plan ? patient.insurance.plan.id : '',
          record: insurance ? patient.insurance.record : '',
          validity: insurance ? patient.insurance.validity : '',
          allergy: patient.allergy ? patient.allergy : false,
        };
      } catch (e) {
        this.$toast.error(e);
      }

      this.loadingPatient = false;
    },
    getCustomName(insurance) {
      if (insurance.plan && insurance.name !== insurance.plan.name) {
        return `${insurance.name} (${insurance.plan.name})`;
      }
      return insurance.name;
    },
    formBlank() {
      return {
        id: null,
        start: false,
        status: '',
        appointmentId: '',
        startDate: '',
        endDate: '',
        private: false,
        screeningId: '',
        professionalId: '',
        patientId: '',
        insurancePlanId: '',
        anthropometry: {
          cephalicPerimeter: '',
          abdominalCircumference: '',
          height: '',
          weight: '',
        },
        vitalSigns: {
          bloodPressure: '',
          respiratoryFrequency: '',
          heartRate: '',
          temperature: '',
          saturation: '',
        },
        reminder: {
          date: '',
          notes: '',
        },
        cids: [],
        anamnesis: {
          templateId: '',
          templateRenderer: null,
          inputs: [],
        },
      };
    },
    showError(e, options = {}) {
      options = { timeout: 15000, ...options };
      this.$toast.error(e, options);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.medical-record-exams {
  $page-header-height: 4.2rem;

  .medical-record-header {
    background-color: $light-color;
    border-bottom: 1px solid $border-color;
    height: $page-header-height;
    position: sticky;
    top: 0;
    z-index: $zindex-0 + 1;
  }

  .card-detail {
    background-color: lighten($gray-color-light, 5%);
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing;
    .card-detail-title {
      font-weight: 600;
      margin-bottom: $layout-spacing-sm;
    }
  }

  .pending-records {
    margin: $layout-spacing-xl;
  }

  .medical-record-form {
    padding: $layout-spacing-lg;
    margin-bottom: $layout-spacing;
    .tab {
      margin-bottom: $layout-spacing;

      a {
        padding-top: 0;
      }
    }
  }

  .medical-record-timer {
    font-size: 1rem;
    font-weight: 500;
    text-align: center;
  }

  .filter-group {
    background-color: $gray-color-light;
    border: $border-width solid $gray-color;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing;
    input {
      &:focus {
        z-index: auto;
      }
    }
  }

  .field-display {
    margin-bottom: $layout-spacing;
    &:last-child {
      margin-bottom: 0;
    }
  }

  .field-display-value {
    border-left: $border-width solid $dark-blue-color-300;
    margin-left: $layout-spacing - $border-width;
    padding-left: $layout-spacing;
    table {
      width: 100%;
      border-collapse: collapse;
      th, td {
        border: $border-width solid $border-color-dark;
        padding: $layout-spacing-sm $layout-spacing;
      }
    }
    p, ul, ol {
      margin: $border-width 0 0 0;
      &:first-of-type {
        margin-top: 0;
      }
    }
    @media screen and (min-width: $size-lg) {
      table {
        width: auto;
        min-width: 50%;
      }
    }
  }

  p {
    margin: 0;
  }
}
</style>
